import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Share2, UserPlus, Gift, MessageCircle, Mail, Link2, Check } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { useTr } from "@/i18n";

const steps = [
  {
    icon: Share2,
    title: "Condividi",
    text: "Inquadra il QR code o manda il link ai tuoi amici con WhatsApp o email.",
  },
  {
    icon: UserPlus,
    title: "Il tuo amico ti cita",
    text: "Nel modulo contatti scrive il tuo nome nel campo “Chi ti ha segnalato?”.",
  },
  {
    icon: Gift,
    title: "Sconto per entrambi",
    text: "Se prenota, lui ha il 10% di sconto sul primo evento e tu sul tuo prossimo.",
  },
];

export default function Referral() {
  const [copied, setCopied] = useState(false);
  const tr = useTr();
  const trSteps = tr("referral.steps", steps);
  const items = steps.map((s, i) => ({ ...s, ...(trSteps[i] || {}) }));
  const siteUrl = `${window.location.origin}/?ref=qr`;
  const shareText = tr(
    "referral.shareText",
    `Ti consiglio DJ Magix per il tuo evento — 25 anni di esperienza, sound anni 80/90, disco dance e non solo. Guarda qui: {url} — se prenoti, scrivi il mio nome nel campo "Chi ti ha segnalato?" del modulo: sconto per entrambi!`
  ).replace("{url}", window.location.origin);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success(tr("referral.copied", "Messaggio copiato — incollalo dove vuoi"));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(tr("referral.copyFail", "Copia non riuscita — seleziona il link manualmente"));
    }
  };

  return (
    <section id="porta-un-amico" className="py-24 bg-[#0B0B0B]" data-testid="referral-section">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          eyebrow={tr("referral.eyebrow", "Passaparola")}
          title={tr("referral.title", "Porta un Amico")}
          accent={tr("referral.accent", "La pubblicità migliore sei tu. Per ogni amico che prenota, lo sconto è doppio: per lui e per te.")}
          testid="referral-heading"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {items.map((s, i) => (
            <motion.div
              key={s.title}
              data-testid={`referral-step-${i + 1}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="gold-frame rounded-2xl bg-[#111111] p-7"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(226,184,87,0.4)] text-[#E2B857]">
                <s.icon size={22} strokeWidth={1.75} />
              </span>
              <p className="font-display text-lg font-semibold text-[#F7DE8B] mt-5">
                <span className="text-[#9F8A52] text-sm mr-2">0{i + 1}</span>
                {s.title}
              </p>
              <p className="text-sm text-[#E2C882]/75 leading-relaxed mt-2">{s.text}</p>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="gold-frame rounded-2xl bg-[#0D0D0D] p-7 sm:p-10 flex flex-col md:flex-row items-center gap-10"
        >
          <div className="shrink-0 text-center" data-testid="referral-qr">
            <div className="inline-block rounded-xl bg-[#F7DE8B] p-4 shadow-[0_0_40px_rgba(226,184,87,0.25)]">
              <QRCodeSVG value={siteUrl} size={150} bgColor="#F7DE8B" fgColor="#0D0D0D" level="M" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BFA45D] mt-4 max-w-[180px]">
              {tr("referral.qrCaption", "Inquadra e apri la gallery del sito")}
            </p>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-display text-xl sm:text-2xl font-semibold gold-text">
              {tr("referral.boxTitle", "Condividi DJ Magix in 10 secondi")}
            </h3>
            <p className="text-sm text-[#E2C882]/80 leading-relaxed mt-3 max-w-xl">
              {tr("referral.boxText", "Mostra il QR code agli amici durante una serata, oppure invia il messaggio già pronto. Quando un amico compila il modulo e inserisce il tuo nome, vi riservo lo sconto a entrambi sul prossimo evento.")}
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-6">
              <a
                data-testid="referral-share-whatsapp"
                href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="gold-btn rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] inline-flex items-center gap-2"
              >
                <MessageCircle size={15} /> WhatsApp
              </a>
              <button
                data-testid="referral-share-email"
                onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent(tr("referral.emailSubject", "Il DJ per il tuo evento — DJ Magix"))}&body=${encodeURIComponent(shareText)}`; }}
                className="rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] inline-flex items-center gap-2 gold-frame text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
              >
                <Mail size={15} /> Email
              </button>
              <button
                data-testid="referral-copy-link"
                onClick={copyLink}
                className="rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] inline-flex items-center gap-2 gold-frame text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
              >
                {copied ? <Check size={15} /> : <Link2 size={15} />}
                {copied ? tr("referral.copiedBtn", "Copiato") : tr("referral.copy", "Copia messaggio")}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
